import { getRequiredIdToken } from '@/lib/get-authenticated-user'

const PLATFORM_ALERTS_WEBHOOK =
  'https://n8n-api.b2breservas.com.br/webhook/platform-alerts'

const inflightAlerts = new Map<string, AbortController>()

export interface NotifyPlatformHttpAlertProps {
  codigoErro: string
  mensagemErro: string
  method: string
  requestBody?: unknown
  requestSearchParams?: unknown
  requestUrl?: string
  usuarioEmail: string
  usuarioNome: string
  [x: string]: unknown
}

interface PlatformAlertPayloadJson {
  codigo_erro: string
  echo_token?: string
  mensagem_erro: string
  payload_front: Record<string, unknown>
  produto: 'BOOKING'
  timestamp_erro: string
  usuario_email: string
  usuario_nome: string
  [x: string]: unknown
}

function safeSerializeJsonValue(value: unknown): unknown {
  if (value === undefined) return undefined
  if (value === null) return null

  const valueType = typeof value
  if (
    valueType === 'string' ||
    valueType === 'number' ||
    valueType === 'boolean'
  )
    return value

  try {
    if (typeof FormData !== 'undefined' && value instanceof FormData) {
      const result: Record<string, unknown> = {}
      for (const [key, v] of value.entries()) {
        if (typeof File !== 'undefined' && v instanceof File) {
          result[key] = { name: v.name, size: v.size, type: v.type }
        } else {
          result[key] =
            typeof Blob !== 'undefined' && v instanceof Blob
              ? { size: v.size }
              : String(v)
        }
      }
      return result
    }

    if (
      typeof URLSearchParams !== 'undefined' &&
      value instanceof URLSearchParams
    ) {
      const result: Record<string, unknown> = {}
      for (const [key, v] of value.entries()) result[key] = v
      return result
    }

    return JSON.parse(JSON.stringify(value))
  } catch {
    return String(value)
  }
}

function extractEchoToken(
  method: string,
  requestUrl?: string,
  requestBody?: unknown,
): string | undefined {
  if (method.toUpperCase() === 'GET' && requestUrl) {
    try {
      const url = new URL(requestUrl, 'http://placeholder')
      return url.searchParams.get('echoToken') ?? undefined
    } catch {
      return undefined
    }
  }

  if (requestBody && typeof requestBody === 'object') {
    return (requestBody as Record<string, unknown>).echoToken as
      | string
      | undefined
  }

  if (typeof requestBody === 'string') {
    try {
      return JSON.parse(requestBody).echoToken
    } catch {
      return undefined
    }
  }

  return undefined
}

function buildAlertKey(props: NotifyPlatformHttpAlertProps): string {
  return JSON.stringify({
    requestUrl: props.requestUrl ?? '',
    codigoErro: props.codigoErro,
    mensagemErro: props.mensagemErro,
    method: props.method,
    requestBody: safeSerializeJsonValue(props.requestBody),
    requestSearchParams: safeSerializeJsonValue(props.requestSearchParams),
  })
}

export async function notifyPlatformHttpAlert({
  method,
  requestUrl,
  requestBody,
  codigoErro,
  mensagemErro,
  usuarioEmail,
  usuarioNome,
  requestSearchParams,
  ...props
}: NotifyPlatformHttpAlertProps): Promise<void> {
  if (requestUrl?.includes('/webhook/platform-alerts')) return

  const alertKey = buildAlertKey({
    codigoErro,
    mensagemErro,
    method,
    usuarioEmail,
    usuarioNome,
    requestBody,
    requestSearchParams,
    requestUrl,
  })

  const previous = inflightAlerts.get(alertKey)
  if (previous) {
    previous.abort()
  }

  const controller = new AbortController()
  inflightAlerts.set(alertKey, controller)

  const echoToken = extractEchoToken(method, requestUrl, requestBody)

  const body: PlatformAlertPayloadJson = {
    produto: 'BOOKING',
    codigo_erro: codigoErro,
    mensagem_erro: mensagemErro,
    usuario_email: usuarioEmail,
    usuario_nome: usuarioNome,
    timestamp_erro: new Date().toISOString(),
    ...(echoToken ? { echo_token: echoToken } : {}),
    payload_front: {
      method,
      url: typeof window !== 'undefined' ? window.location.href : '',
      ...(requestBody !== undefined
        ? { requestBody: safeSerializeJsonValue(requestBody) }
        : {}),
      ...(requestSearchParams !== undefined
        ? {
            requestSearchParams: safeSerializeJsonValue(requestSearchParams),
          }
        : {}),
      ...props,
    },
    ...props,
  }

  try {
    const jwtToken = await getRequiredIdToken()

    await fetch(PLATFORM_ALERTS_WEBHOOK, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: jwtToken,
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
  } catch {
    // AbortError or network failure -- must not break the caller's error handling.
  } finally {
    if (inflightAlerts.get(alertKey) === controller) {
      inflightAlerts.delete(alertKey)
    }
  }
}
